import { useEffect } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { RATES } from "../../lib/rates";
import Stepper from "../shared/Stepper";
import Step1Dach from "./Step1Dach";
import Step2Verbrauch from "./Step2Verbrauch";
import Step3Speicher from "./Step3Speicher";
import Step4Ergebnis from "./Step4Ergebnis";

export interface ROIFormData {
  // Step 1
  dachflaeche_m2?: number;
  ausrichtung?: "sued" | "suedost" | "suedwest" | "ost" | "west" | "ost_west";
  dachneigung?: number;
  // Step 2
  jahresverbrauch_kwh?: number;
  strompreis_ct?: number;
  postleitzahl?: string;
  // Step 3
  mit_speicher?: boolean;
  speicher_kwh?: number;
  einspeiseverguetung_ct?: number;
  // Paket
  paket?: "basic" | "premium";
}

const STEPS = [
  { label: "Dach" },
  { label: "Verbrauch" },
  { label: "Speicher" },
  { label: "Ergebnis" },
];

const ROI_DEFAULTS: ROIFormData = {
  dachneigung: 30,
  strompreis_ct: RATES.strom.haushaltsstrompreis_ct_kwh.value,
  einspeiseverguetung_ct: RATES.strom.einspeiseverguetung_ct_kwh.value,
  mit_speicher: false,
};

export default function ROISimulator() {
  const [step, setStep] = usePersistedState("neko-roi-step", 1);
  const [formData, setFormData] = usePersistedState<ROIFormData>("neko-roi-data", ROI_DEFAULTS);

  const update = (data: Partial<ROIFormData>) =>
    setFormData((prev) => ({ ...prev, ...data }));

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  // Phase 7 / WEB-04: cookieless tool-open event — fires once on mount.
  // No PII — only tool name and tenant_id from server-injected meta tag.
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
      const slugMeta = document.querySelector('meta[name="x-tenant-slug"]');
      const tenantId = slugMeta?.getAttribute("content") ?? "unknown";
      window.plausible("tool_opened", {
        props: { tool: "roi-simulator", tenant_id: tenantId },
      });
    }
  }, []);

  return (
    <div className="animate-fade-in" data-testid="roi-simulator">
      <h1 className="text-xl font-bold text-neko-white mb-1">Solar-Rechner</h1>
      <p className="text-neko-gray text-sm mb-6">
        Berechnen Sie die Rendite Ihrer Photovoltaik-Anlage über 25 Jahre.
      </p>

      <Stepper steps={STEPS} currentStep={step} />

      {step === 1 && <Step1Dach data={formData} onChange={update} onNext={next} />}
      {step === 2 && <Step2Verbrauch data={formData} onChange={update} onNext={next} onBack={back} />}
      {step === 3 && <Step3Speicher data={formData} onChange={update} onNext={next} onBack={back} />}
      {step === 4 && <Step4Ergebnis data={formData} onBack={back} onReset={() => {
            setStep(1);
            setFormData(ROI_DEFAULTS);
            sessionStorage.removeItem("neko-roi-step");
            sessionStorage.removeItem("neko-roi-data");
          }} />}
    </div>
  );
}
